// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { getPerformance } from "firebase/performance";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { enableNetwork, connectFirestoreEmulator, initializeFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

// Production defaults — used when no VITE_FIREBASE_* env vars are set.
// If env vars ARE set but empty, that's a misconfiguration — warn and fail fast.
const PROD_DEFAULTS = {
  apiKey: "AIzaSyCBXxF9eR2e-oKVkQXX9GWyBEbvYa0XgRQ",
  authDomain: "quantum-flo-auto-algo-d3c2b.firebaseapp.com",
  projectId: "quantum-flo-auto-algo-d3c2b",
  storageBucket: "quantum-flo-auto-algo-d3c2b.firebasestorage.app",
  messagingSenderId: "236722068594",
  appId: "1:236722068594:web:70d265c293d28adf80b6eb",
  measurementId: "G-BY9N8429TK"
};

/**
 * Resolve a Firebase config value from env var with safe fallback.
 * - If the env var is undefined → use the production default (local dev without .env).
 * - If the env var is set but empty string → treat as misconfiguration and throw.
 */
function resolveConfigValue(envValue, defaultValue, envName) {
  if (envValue === undefined) return defaultValue;
  if (envValue === '') {
    throw new Error(
      `Firebase config error: ${envName} is set but empty. ` +
      `This would silently fall back to production credentials. ` +
      `Either provide a valid value or remove the variable entirely.`
    );
  }
  return envValue;
}

const firebaseConfig = {
  apiKey: resolveConfigValue(import.meta.env.VITE_FIREBASE_API_KEY, PROD_DEFAULTS.apiKey, 'VITE_FIREBASE_API_KEY'),
  authDomain: resolveConfigValue(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, PROD_DEFAULTS.authDomain, 'VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: resolveConfigValue(import.meta.env.VITE_FIREBASE_PROJECT_ID, PROD_DEFAULTS.projectId, 'VITE_FIREBASE_PROJECT_ID'),
  storageBucket: resolveConfigValue(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET, PROD_DEFAULTS.storageBucket, 'VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: resolveConfigValue(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID, PROD_DEFAULTS.messagingSenderId, 'VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: resolveConfigValue(import.meta.env.VITE_FIREBASE_APP_ID, PROD_DEFAULTS.appId, 'VITE_FIREBASE_APP_ID'),
  measurementId: resolveConfigValue(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, PROD_DEFAULTS.measurementId, 'VITE_FIREBASE_MEASUREMENT_ID')
};

// Only allow emulator connections in dev builds to prevent accidental use in production
const isEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true';

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
let analytics = null;
let performance = null;

const auth = getAuth(app);

// Initialize Firestore with better configuration
const db = initializeFirestore(app, {
  experimentalForceLongPolling: true, // Use long polling instead of WebSocket
  useFetchStreams: false, // Disable fetch streams to avoid QUIC issues
  cacheSizeBytes: 50 * 1024 * 1024, // 50MB cache
});

const storage = getStorage(app);

// Connect to emulators in local/test mode
if (isEmulator) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 9098);
  console.log('🔧 Connected to Firebase emulators');
} else {
  // Analytics & Performance only in production/staging (not emulators)
  try {
    analytics = getAnalytics(app);
    performance = getPerformance(app);
    console.log('📊 Firebase Analytics & Performance initialized');
  } catch (error) {
    console.warn('Performance initialization failed:', error);
  }

  // Enable network connection with retry logic
  const enableFirestoreNetwork = async () => {
    try {
      await enableNetwork(db);
      console.log('✅ Firestore network enabled successfully');
    } catch (error) {
      console.error('❌ Failed to enable Firestore network:', error);
      setTimeout(enableFirestoreNetwork, 2000);
    }
  };
  enableFirestoreNetwork();
}

// Helper function to log custom events
export const logCustomEvent = (eventName, eventParams = {}) => {
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  }
};

// Export the services for use in other parts of the app
export { app, analytics, performance, auth, db, storage };
export default app;